import { Transition } from "@headlessui/react";
import { Head, useForm, usePage } from "@inertiajs/react";
import { type FormEventHandler, useRef } from "react";

import HeadingSmall from "@/components/heading-small";
import InputError from "@/components/input-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AppLayout from "@/layouts/app-layout";
import SettingsLayout from "@/layouts/settings/layout";
import { settingsEmailPath } from "@/routes";
import type { BreadcrumbItem, SharedData } from "@/types";

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Configuración de correo electrónico",
    href: settingsEmailPath(),
  },
];

interface EmailForm {
  password_challenge: string;
  email: string;
}

export default function Email() {
  const currentPasswordInput = useRef<HTMLInputElement>(null);

  const { auth } = usePage<SharedData>().props;
  const {
    data,
    setData,
    patch,
    errors,
    reset,
    processing,
    recentlySuccessful,
  } = useForm<Required<EmailForm>>({
    password_challenge: "",
    email: auth.user.email,
  });

  const submit: FormEventHandler = (e) => {
    e.preventDefault();

    patch(settingsEmailPath(), {
      preserveScroll: true,
      onSuccess: () => reset("password_challenge"),
      onError: (errors) => {
        if (errors.password_challenge) {
          reset("password_challenge");
          currentPasswordInput.current?.focus();
        }
      },
    });
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Actualizar correo electrónico"
            description="Actualiza tu dirección de correo electrónico y verifícala"
          />

          <form onSubmit={submit} className="space-y-6">
            <div className="grid gap-2">
              <Label htmlFor="email">Correo electrónico</Label>

              <Input
                id="email"
                type="email"
                className="mt-1 block w-full"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
                required
                autoComplete="username"
                placeholder="Correo electrónico"
              />

              <InputError className="mt-2" message={errors.email} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="password_challenge">Contraseña actual</Label>

              <Input
                id="password_challenge"
                ref={currentPasswordInput}
                value={data.password_challenge}
                onChange={(e) => setData("password_challenge", e.target.value)}
                type="password"
                className="mt-1 block w-full"
                autoComplete="current-password"
                placeholder="Contraseña actual"
              />

              <InputError message={errors.password_challenge} />
            </div>

            <div className="flex items-center gap-4">
              <Button disabled={processing}>Guardar</Button>

              <Transition
                show={recentlySuccessful}
                enter="transition ease-in-out"
                enterFrom="opacity-0"
                leave="transition ease-in-out"
                leaveTo="opacity-0"
              >
                <p className="text-sm text-neutral-600">Guardado</p>
              </Transition>
            </div>
          </form>
        </div>
      </SettingsLayout>
    </AppLayout>
  );
}
